import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry, shareReplay } from 'rxjs/operators';

import { AjaxService } from 'Angular/services/core/ajax.service';

const STATIC_FILE_MANIFEST_URL =
    `${process.env.STATIC_BASE_URL}/staticfiles` +
    `${process.env.YC_STATIC_VERSION != null ? `.${process.env.YC_STATIC_VERSION}` : ''}.json`;

/**
 * Static File Url Service
 *
 * This service gets the cached URL
 * of a static asset when provided its
 * static path.
 *
 */

interface StaticFileManifest {
    paths: {
        [k: string]: string;
    };
    version: string;
}

@Injectable({
    providedIn: 'root',
})
export class StaticFileUrlService {
    staticFileManifest: Observable<StaticFileManifest>;

    constructor(private ajaxService: AjaxService) {
        this.staticFileManifest = this.ajaxService
            // Cache the response for 5 minutes. We also specify the following response headers in nginx_static_assets.conf:
            //  * must-revalidate: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control#revalidation_and_reloading
            //  * s-maxage: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control#expiration
            .getDataObserveBody<StaticFileManifest>(STATIC_FILE_MANIFEST_URL, {}, { 'Cache-Control': 'max-age=300' })
            .pipe(
                // shareReplay allows multiple subscribers to subscribe to this observable
                // without triggering additional HTTP requests.
                shareReplay(1),
                // If we fail to get the static file manifest retry twice.
                retry(2)
            );
    }

    /**
     * Returns the relative static URL via the media directory. (ie. /media/images/example.f12fr4sa.jpg)
     * If we fail to get the manifest then return the non-cached version of the file (ie. /media/images/example.jpg)
     *
     * @param staticUrl - The static path of the asset being requested. (ie. images/example.jpg)
     */
    getRelativeUrl(staticUrl: string): Observable<string> {
        const fallbackUrl = `/media/${staticUrl}`;

        return this.staticFileManifest.pipe(
            map((data: StaticFileManifest) => {
                const urlIndex = data.paths;
                const cachedUrl = urlIndex[staticUrl];
                return cachedUrl === undefined ? fallbackUrl : `/media/${cachedUrl}`;
            }),
            catchError(() => {
                return of(fallbackUrl);
            })
        );
    }

    /**
     * Returns the absolute static URL to our CDN. (ie. static.ycharts.com/images/example.f12fr4sa.jpg)
     * If we fail to get the manifest then return the non-cached
     * version of the file (ie. static.ycharts.com/images/example.jpg)
     *
     * @param staticUrl - The static path of the asset being requested. (ie. images/example.jpg)
     */
    getAbsoluteURL(staticUrl: string): Observable<string> {
        const staticBaseUrl = `${process.env.STATIC_BASE_URL}`;
        const fallbackUrl = `${staticBaseUrl}/${staticUrl}`;

        return this.staticFileManifest.pipe(
            map((data: StaticFileManifest) => {
                const urlIndex = data.paths;
                const cachedUrl = urlIndex[staticUrl];
                return cachedUrl === undefined ? fallbackUrl : `${staticBaseUrl}/${cachedUrl}`;
            }),
            catchError(() => {
                return of(fallbackUrl);
            })
        );
    }
}
